import React, { useEffect, useState } from 'react';
import { Grid, Button, Typography, CircularProgress, IconButton, Container, Stack, MenuItem, Select, SelectChangeEvent, Paper } from '@mui/material';
import { useSelector } from 'react-redux';
import { User } from '../../redux-toolkit/reducers/userReducer';
import { RootState } from '../../redux-toolkit/store/stateType';
import { createGoal, fetchCurrentGoals, updateGoal } from '../../services/goalService';
import GoalsCard from './GoalsCard';

interface RecordType {
    goalId: string;
    goalStartDate: number;
    goalEndDate: number;
    goals: string;
    organisationId: string;
    userName: string;
    employeeType: string;
    leadId: string;
}

const MyGoals: React.FC = () => {
    const user: User = useSelector((state: RootState) => state.user);
    const [records, setRecords] = useState<RecordType[]>([]);
    const [loading, setLoading] = useState(true);
    const [myGoals, setMyGoals] = useState<any[]>([]);
    const [filteredRecords, setFilteredRecords] = useState<RecordType[]>([]);
    const currentYear = new Date().getFullYear().toString();
    const [selectedYear, setSelectedYear] = useState<string>(currentYear);

    const handleYearChange = (event: SelectChangeEvent<string>) => {
        const year = event.target.value as string;
        setSelectedYear(year);

        if (year && records && Array.isArray(records)) {
            const filtered = records.filter((record) => {
                if (!record || !record.goalStartDate) return false;
                try {
                    return new Date(record.goalStartDate * 1000).getFullYear().toString() === year;
                } catch (error) {
                    console.error('Error parsing date:', error);
                    return false;
                }
            });
            setFilteredRecords(filtered);
        } else {
            setFilteredRecords(records || []);
        }
    };

    useEffect(() => {
        const fetchGoalsData = async () => {
            setLoading(true);
            try {
                if (!user || !user.userName) {
                    console.error('User or userName is not available');
                    setLoading(false);
                    return;
                }

                const myGoalsResponse = await fetchCurrentGoals('', user.userName);
                const fetchedRecords = myGoalsResponse?.data?.res?.Items || [];

                // Validate fetched records
                const validRecords = Array.isArray(fetchedRecords) ? fetchedRecords.filter((record: any) =>
                    record && typeof record === 'object' && record.goalStartDate
                ) : [];

                setRecords(validRecords);

                // Filter by current year with null checks
                const filtered = validRecords.filter((record: RecordType) => {
                    if (!record || !record.goalStartDate) return false;
                    try {
                        return new Date(record.goalStartDate * 1000).getFullYear().toString() === currentYear;
                    } catch (error) {
                        console.error('Error parsing date for record:', record, error);
                        return false;
                    }
                });
                setFilteredRecords(filtered);
            } catch (error) {
                console.error('Error fetching goals:', error);
                setRecords([]);
                setFilteredRecords([]);
            } finally {
                setLoading(false);
            }
        };

        fetchGoalsData();
    }, [user?.userName, currentYear]);

    // Helper function to safely format dates
    const formatDate = (timestamp: number) => {
        try {
            if (!timestamp || isNaN(timestamp)) return 'Invalid Date';
            return new Date(timestamp * 1000).toLocaleDateString();
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Invalid Date';
        }
    };

    // Get unique years for dropdown with null checks
    const getAvailableYears = () => {
        if (!records || !Array.isArray(records)) return [];

        const years = records
            .filter(record => record && record.goalStartDate)
            .map(record => {
                try {
                    return new Date(record.goalStartDate * 1000).getFullYear();
                } catch (error) {
                    console.error('Error extracting year:', error);
                    return null;
                }
            })
            .filter(year => year !== null);

        return Array.from(new Set(years)).sort((a: any, b: any) => b - a);
    };

    return (
        <div>
            {loading ? (
                <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100vh' }}>
                    <CircularProgress />
                </Grid>
            ) : (
                <Container sx={{ padding: '10px', borderRadius: '5px' }} maxWidth={false}>
                    <Stack direction="row" spacing={2} alignItems={'center'} justifyContent="flex-end" marginBottom={2}>
                        <Stack direction={'row'} spacing={2}>
                            <Typography variant="h6">Filter by Year:</Typography>
                            <Select
                                value={selectedYear}
                                onChange={handleYearChange}
                                displayEmpty
                                sx={{ minWidth: 120 }}
                                size='small'
                            >
                                {getAvailableYears().map((year: any) => (
                                    <MenuItem key={year} value={year?.toString() || ''}>
                                        {year || 'Unknown'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                    </Stack>

                    {!filteredRecords || filteredRecords.length === 0 ? (
                        <Paper elevation={2} sx={{ padding: '20px', borderRadius: '5px', textAlign: 'center' }}>
                            <Typography variant="h6" color="textSecondary">
                                No goals found for the selected year.
                            </Typography>
                        </Paper>
                    ) : (
                        filteredRecords.map((record: any, index: number) => {
                            if (!record) return null;

                            return (
                                <Paper key={`${record.goalId || index}`} elevation={2} sx={{ padding: '10px', borderRadius: '5px', marginBottom: '10px' }}>
                                    <Stack direction={'row'} spacing={10} mt={2}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            <strong>Goal Start Date:</strong> {formatDate(record.goalStartDate)}
                                        </Typography>
                                        <Typography variant="subtitle1" gutterBottom>
                                            <strong>Goal End Date:</strong> {formatDate(record.goalEndDate)}
                                        </Typography>
                                    </Stack>
                                    <GoalsCard goalsData={record?.goals || []} />
                                </Paper>
                            );
                        })
                    )}
                </Container>
            )}
        </div>
    );
};

export default MyGoals;